import * as queries from './queries';
import app from '../../app';
import gql from 'graphql-tag';

import { getTimeConditionOperator } from '../utils';

const alertEmergencyLevels = new Map([
  ['yellow', 1],
  ['orange', 2],
  ['red', 3],
]);

const alertStates = new Map([
  ['active', 'active'],
  ['handled', 'handled'],
]);

export function getAlertsQueryConditions(fromDate, toDate, filter, recent = false) {
  const dates = [fromDate, toDate].filter((item) => !!item)

  const dateConditions = (fromDate || toDate)
    ? [{
      field: 'activateDate',
      operator: getTimeConditionOperator(fromDate, toDate),
      parameters: dates,
    }] : [];

  const levelConditions = filter.length && filter.some((item) => alertEmergencyLevels.has(item))
    ? [{
      field: 'emergencyLevel',
      operator: 'in',
      parameters: filter.filter(
        (item) => alertEmergencyLevels.has(item)
      ).map((item) => alertEmergencyLevels.get(item).toString()),
    }] : [];

  const stateCondtions = filter?.length && filter?.some((item) => alertStates.has(item)) ? [{
    field: 'state',
    operator: 'in',
    parameters: filter.filter(
      (item) => alertStates.has(item)
    ).map((item) => alertStates.get(item).toString()),
  }] : [{
    field : 'state',
    operator: 'in'
    , parameters : ['active', 'handled']  /*alerts in dashboard only care about active and handled states */
  }];

  const recentConditions = recent ? [{
    function: "sinceLastSDMEncounterEnd"
  }] : [];

  return {
    conditions: {
      gate: 'and',
      inputs: [
        ...stateCondtions,
        ...dateConditions,
        ...levelConditions,
        ...recentConditions,
      ],
    },
  };
}

export async function getRecent({ dispatch, commit }, patientId) {
  if (!patientId) return;

  try {
    const { data, errors } = await app.apolloClient.query({
      query: queries.getRecentAlerts,
      variables: { patientId },
    });

    if (data && !errors) {
      commit('setRecent', data.alerts.edges);
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export async function sendReminderToPatient({ dispatch }, {
  patientId,
  reminderType,
  force = false,
  forceNotify = false,
  parameters = []
}) {
  try {
    const { data, errors } = await app.apolloClient.query({
      query: queries.sendReminderToPatient,
      variables: {
        patientId: parseInt(patientId),
        reminderType,
        force,
        forceNotify,
        parameters
      },
    });

    if (data && !errors) {
      return data;
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }

  return null;
}

export async function getAll({ dispatch, commit, state }) {
  const {
    patient,
    fromDate,
    toDate,
    limit,
    offset,
    filters,
  } = state;

  const endOfToDate = toDate ? toDate + ' 23:59:59' : toDate;

  try {
    const { data, errors } = await app.apolloClient.query({
      query: queries.getAlerts,
      variables: {
        ...(patient && patient.patientId && {
          functions: [
            {
              function: 'patientId',
              parameters: patient.patientId,
            },
          ],
        }),
        ...getAlertsQueryConditions(fromDate, endOfToDate, filters),
        limit,
        offset,
      },
    });

    if (data && !errors) {
      commit('setAll', filters.length ? data.alerts.edges : []);
      commit('setPageInfo', filters.length ? data.alerts.pageInfo : null);
      commit('setCount', filters.length ? data.alerts.count : 0);
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export function selectPatient({ dispatch, commit }, patient) {
  commit('selectPatient', patient);
  commit('setOffset', 0);
  dispatch('getAll');
}

export function setLimit({ dispatch, commit }, limit) {
  commit('setLimit', limit);
  commit('setOffset', 0);
  dispatch('getAll');
}

export function setOffset({ dispatch, commit }, offset) {
  commit('setOffset', offset);
  dispatch('getAll');
}

export function setFromDate({ dispatch, commit }, fromDate) {
  commit('setFromDate', fromDate);
  commit('setOffset', 0);
  dispatch('getAll');
}

export function setToDate({ dispatch, commit }, toDate) {
  commit('setToDate', toDate);
  commit('setOffset', 0);
  dispatch('getAll');
}

export function setFilters({ dispatch, commit }, filters) {
  commit('setFilters', filters);
  commit('setOffset', 0);
  dispatch('getAll');
}

export async function handleOrReactivateRecent({ dispatch, commit }, {alertId, reactivate = false}) {
  try {
    const { data, errors } = await app.apolloClient.query({
      query: queries.handleOrReactivateAlert(reactivate),
      variables: { alertId },
    });

    if (data && !errors) {
      commit('findAndUpdateRecent', data[reactivate ? 'reactivateAlert' : 'handleAlert']);
      return true;
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }

    return false;
  } catch (error) {
    dispatch('error/submit', error, { root: true });
    return false;
  }
}

export async function handleOrReactivateAll({ dispatch }, { alertId, reactivate = false}) {
  try {
    const { data, errors } = await app.apolloClient.query({
      query: queries.handleOrReactivateAlert(reactivate),
      variables: { alertId },
    });

    if (data && !errors) {
      setTimeout(() => {
        dispatch('getAll');
      }, 2000);
      return true;
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }

    return false;
  } catch (error) {
    dispatch('error/submit', error, { root: true });
    return false;
  }
}

export async function reset({ commit }) {
  commit('reset');
}

export async function sendMultipleRemindersToPatient({ dispatch }, {
  documentIds,
  patientId,
  reminderType,
  force = false,
  forceNotify = false,
  parameterKey
}) {

  if (!documentIds || !documentIds.length) return;

  const parameterVariableString = documentIds.map((id, i) => {
    return `$parameters_${i}: [ParameterInput!]!`
  }).join(', ');

  const parameterVariables = documentIds.reduce((obj, curr, i) => {
    return { ...obj, [`parameters_${i}`]: [{ key: parameterKey, value: curr }] }
  }, {})

  const variables = {
    patientId: parseInt(patientId),
    reminderType,
    force,
    forceNotify,
    ...parameterVariables
  };

  const variableString = `
    $patientId: Int!,
    $reminderType: String!,
    $force: Boolean!,
    $forceNotify: Boolean!,
    ${parameterVariableString}
  `;

  const queryString = documentIds.map((id, i) => {
    return `
      checkReminder_${i} : checkReminder(
        patientId : $patientId
        handlerType : $reminderType
        force : $force
        forceNotify : $forceNotify
        parameters : $parameters_${i}
      ){
        id
      }
    `
  }).join(' ');

  try {
    const { data, errors } = await app.apolloClient.query({
      query: gql`
        mutation sendReminderToPatient(${variableString}) {
          ${queryString}
        }
      `, variables: variables,
    });

    if (data && !errors) {
      return data;
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
    return false;
  }
}